import './ForgotPassword.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';

import HttpService from '../../app/http';
import Alert, { IAlertType } from '../../components/common/Alert';
import SiteFooterComponent from '../../components/site/site-footer.component';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import useAuth from '../../hooks/useAuth';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';

export const Timer = ({ setShowTimer, ttl }: { setShowTimer: (bool: boolean) => void; ttl: number }) => {
  const { t } = useTranslation();
  const [remain, setRemain] = useState(ttl);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemain(remain - 1);

      if (remain <= 0) setShowTimer(false);
    }, 1000);

    return () => clearInterval(interval);
  }, [remain, setShowTimer, ttl]);

  return (
    <span>
      {remain} {t('authPages/forgotPassword/l-01')}
    </span>
  );
};

const ForgotPassword = () => {
  const { i18n, t } = useTranslation();
  const schema = Yup.object({
    user_email: Yup.string()
      .required()
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        t(AlertsType.ISATKO003)
      )
      .label('Email')
      .email(),
  }).required();
  const [toast, setToast] = useState<IAlertType>({
    isShow: false,
    title: '',
    message: '',
    type: 'Error',
  });
  const navigate = useNavigate();
  const auth = useAuth();

  const from = `/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`;

  useEffect(() => {
    let cleaner = true;

    if (cleaner && auth.user.isAuthenticated) navigate(from);

    return () => {
      cleaner = false;
    };
  }, [auth, from, navigate]);

  const [showTimer, setShowTimer] = useState(false);
  const [ttl, setTtl] = useState(60);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<{ user_email: string }>({
    resolver: yupResolver(schema),
  });

  const ForgotPasswordMutation = useMutation<any, AxiosError<any, any>>(
    user_email =>
      HttpService.get(`auth/password`, {
        params: {
          email_address: user_email,
        },
      }),
    {
      onError: err => {
        //toast.error(err.response?.data.message || err.message);

        setShowTimer(false);
      },
    }
  );

  const onSubmit = (data: any) => {
    localStorage.setItem(
      `LOUISEBOT_ForgetPassword`,
      JSON.stringify({
        date: Date.now().toString(),
        email: data.user_email,
      })
    );
    closeToast();
    setShowTimer(true);
    ForgotPasswordMutation.mutate(data.user_email, {
      onSuccess: () => {
        // toast.success(t(AlertsType.ISATKO014));
      },
      onError: res => {
        if (res?.response?.status && res?.response?.status != 500) {
          setToast({
            isShow: true,
            message: res?.response?.data?.message,
            title: 'Warning',
            type: 'warning',
          });
        }
      },
    });
  };

  const closeToast = () => {
    setToast({
      isShow: false,
      message: '',
      title: '',
      type: 'warning',
    });
  };

  useEffect(() => {
    const data = localStorage.getItem(`LOUISEBOT_ForgetPassword`);
    if (data) {
      const { date, email } = JSON.parse(data);
      if (email) {
        setValue('user_email', email);
      }
      if (date) {
        const firstDate = new Date(+date);
        const LastDate = new Date(Date.now());
        const diffTime = Math.abs(+LastDate - +firstDate);
        const secondTime = Math.ceil(diffTime / 1000);

        if (secondTime < 60) {
          setShowTimer(true);
          setTtl(60 - secondTime);
        } else {
          setTtl(60);
          localStorage.removeItem(`LOUISEBOT_ForgetPassword`);
        }
      } else {
        localStorage.removeItem(`LOUISEBOT_ForgetPassword`);
      }
    }
  }, []);

  return (
    <>
      {/* <SiteNavbarComponent theme="light" /> */}
      <div className="d-flex flex-column align-items-center justify-content-center">
        <Container>
          <Row className="justify-content-center tw-mt-20">
            <Col lg={5} md={10} sm={10} xs={12}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="forgot-password-title w-100 text-center">{t('authPages/forgotPassword/title')}</h1>
              </div>

              <div>
                {toast.isShow && (
                  <Alert type={toast.type} onClick={() => closeToast()}>
                    {toast.message}
                  </Alert>
                )}
              </div>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="user_email" className="forgot-password-label">
                    {t('authPages/forgotPassword/l-02')}
                  </Form.Label>
                  <Form.Control className="forgot-password-input" id="user_email" type="email" isInvalid={!!errors.user_email} {...register('user_email')} />
                  <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                    {errors?.user_email?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <PSButton
                  id="btn-forget-password-submit"
                  variant="primary"
                  type="submit"
                  className="d-flex justify-content-center w-100 "
                  size="lg"
                  disabled={showTimer || ForgotPasswordMutation.isLoading}
                  isLoading={ForgotPasswordMutation.isLoading}
                >
                  {showTimer ? <Timer setShowTimer={setShowTimer} ttl={ttl} /> : <span>{t('authPages/forgotPassword/btn-01')}</span>}
                </PSButton>

                <div className="w-100 text-center mt-3">
                  <small className=" forgot-password">
                    <span className="tw-me-1">{t('authPages/forgotPassword/text-01')}</span>
                    <Link to="../login" className="color-twelveColor fw-bold text-decoration-underline tw-whitespace-nowrap">
                      {t('authPages/forgotPassword/l-03')}
                    </Link>
                  </small>
                </div>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>
      <SiteFooterComponent theme="light" className="tw-absolute tw-bottom-0 tw-w-full" />
    </>
  );
};

export default WrappedErrorBoundary(ForgotPassword);
